/**
 * Ukrainian interface.
 *
 * The keys are the English source strings — see lib/i18n.tsx for why. A
 * key that is missing here simply renders in English, so this file can
 * never break the UI and can always be extended.
 *
 * Terminology, fixed once so the app reads as one voice:
 *   block/segment  блок / сегмент      take        дубль
 *   dub            дубляж              merge       зведення
 *   arrange        монтаж              crop        наріз
 *   reference      референс            stems       стеми
 *   pace           темп                seed        сід
 *   track          доріжка             credits     кредити
 */
export const uk: Record<string, string> = {
  // ── Shell, navigation, account ────────────────────────────────────────
  "Neo Dub Studio": "Neo Dub Studio",
  Home: "Головна",
  Projects: "Проєкти",
  "My projects": "Мої проєкти",
  "Shared with me": "Доступні мені",
  Account: "Акаунт",
  "Account Settings": "Налаштування акаунта",
  "Account overview": "Огляд акаунта",
  Settings: "Налаштування",
  Billing: "Оплата",
  Admin: "Адмін",
  Profile: "Профіль",
  Security: "Безпека",
  Language: "Мова",
  "Audio output": "Аудіовихід",
  "Which device this app's sound plays through — independent of every other app.":
    "На який пристрій виводиться звук цього застосунку — незалежно від усіх інших програм.",
  "System default": "Системний за умовчанням",
  "Choose device…": "Обрати пристрій…",
  "Use system default": "Використати системний за умовчанням",
  "Not available in this app or browser yet — change it in your system's sound settings instead.":
    "Ще недоступно в цьому застосунку чи браузері — поки що змініть це в налаштуваннях звуку системи.",
  Beta: "Бета",
  "Signing in...": "Вхід...",
  "Don't have an account yet?": "Ще немає акаунта?",
  "Could not reach the server": "Не вдалося зв'язатися із сервером",
  "Too many attempts. Try again in a few minutes.":
    "Забагато спроб. Спробуйте за кілька хвилин.",
  "Invalid email or password": "Невірна пошта або пароль",
  "Neo Noise": "Neo Noise",
  "Coming soon": "Незабаром",
  "Live test": "Живий тест",
  Pricing: "Тарифи",
  Docs: "Документація",
  Contact: "Контакти",
  Dubbing: "Дубляж",
  Voiceover: "Озвучення",
  "Neo Script": "Neo Script",
  "Sign in": "Увійти",
  "Sign out": "Вийти",
  "Navigation menu": "Меню навігації",
  "Close menu": "Закрити меню",
  "Back to home": "На головну",
  "Back to projects": "До проєктів",
  "Manage your profile, security and usage": "Профіль, безпека та використання",
  "The language of this interface. It does not change anything about your projects or your dubs.":
    "Мова цього інтерфейсу. На ваші проєкти та дубляжі вона не впливає.",
  English: "Англійська",
  Ukrainian: "Українська",

  // ── Auth ──────────────────────────────────────────────────────────────
  Email: "Пошта",
  Password: "Пароль",
  "Your password": "Ваш пароль",
  "Confirm password": "Підтвердіть пароль",
  "Create one": "Створити",
  "Create your account": "Створіть акаунт",
  "Passwords do not match.": "Паролі не збігаються.",
  "New password and confirmation don't match": "Новий пароль і підтвердження не збігаються",
  "Password updated": "Пароль оновлено",
  "Fill in your current and new password": "Введіть поточний і новий пароль",
  "Use a strong password — at least 8 characters.":
    "Використайте надійний пароль — щонайменше 8 символів.",
  "Delete account": "Видалити акаунт",
  "Permanently deletes your account and every project, upload and generated take in it. This cannot be undone and we cannot recover anything afterwards. Export what you want to keep first.":
    "Назавжди видаляє ваш акаунт і всі проєкти, завантаження та згенеровані дублі в них. Дію не можна скасувати, і відновити щось згодом ми не зможемо. Спершу експортуйте те, що хочете зберегти.",

  // ── Projects list, sharing ────────────────────────────────────────────
  Create: "Створити",
  "New project name": "Назва нового проєкту",
  "No projects yet — create your first one above.":
    "Проєктів ще немає — створіть перший угорі.",
  "Could not create project": "Не вдалося створити проєкт",
  "Delete project": "Видалити проєкт",
  "Project deleted": "Проєкт видалено",
  "Could not delete project": "Не вдалося видалити проєкт",
  Share: "Поділитися",
  "Share project": "Поділитися проєктом",
  "Project shared": "Доступ надано",
  "Share this project with another account": "Надати доступ до проєкту іншому акаунту",
  "Shared with {count} accounts": "Доступ у {count} акаунтів",
  "Not shared with anyone yet.": "Доступ ще нікому не надано.",
  "Access level": "Рівень доступу",
  "Read-only": "Лише перегляд",
  "Full access": "Повний доступ",
  "Remove access": "Забрати доступ",
  "The other account sees it under &ldquo;Shared with me&rdquo;. Read-only can watch, listen and read; full access can also edit and generate.":
    "Інший акаунт побачить його в розділі «Доступні мені». Лише перегляд — дивитися, слухати й читати; повний доступ — ще й редагувати та генерувати.",
  "Project history": "Історія проєкту",
  "Activity history — who did what in this project":
    "Історія дій — хто що робив у цьому проєкті",
  "Every processing action ever run in this project and who started it. Newest first.":
    "Усі обробки, що будь-коли запускалися в цьому проєкті, і хто їх почав. Найновіші вгорі.",
  "Nothing has been run in this project yet.": "У цьому проєкті ще нічого не запускали.",

  // ── Studio: common ────────────────────────────────────────────────────
  Transcribe: "Транскрибувати",
  Generation: "Генерація",
  Arrange: "Монтаж",
  Import: "Імпорт",
  Export: "Експорт",
  Save: "Зберегти",
  Saved: "Збережено",
  Delete: "Видалити",
  Close: "Закрити",
  Done: "Готово",
  Stop: "Стоп",
  Stopped: "Зупинено",
  "Stopping...": "Зупиняється...",
  "Running...": "Виконується...",
  Generating: "Генерується",
  "Loading…": "Завантаження…",
  "Loading waveform…": "Завантаження хвилі…",
  "Couldn&apos;t load waveform": "Не вдалося завантажити хвилю",
  "No matches.": "Нічого не знайдено.",
  "Search by word…": "Пошук за словом…",
  Use: "Застосувати",
  Used: "Витрачено",
  Actions: "Дії",
  Position: "Позиція",
  Player: "Плеєр",
  Volume: "Гучність",
  "Volume level": "Рівень гучності",
  "Preview volume": "Гучність прослуховування",
  "Preview playback volume": "Гучність прослуховування",
  Space: "Пробіл",
  "Space – play": "Пробіл – відтворити",
  "click – seek": "клік – перемотати",
  "drag – move": "тягнути – пересунути",
  "edge drag – trim": "тягнути за край – підрізати",
  "Alt+click – cut": "Alt+клік – розрізати",
  "Toggle auto-scroll": "Автопрокрутка",
  "Credit balance — click to top up": "Баланс кредитів — натисніть, щоб поповнити",
  Credits: "Кредити",
  "Generation Credits": "Кредити на генерацію",
  Balance: "Баланс",
  Buy: "Купити",
  "Manage billing": "Керувати оплатою",

  // ── Step 1: transcription ─────────────────────────────────────────────
  "Upload media": "Імпорт медіа",
  "Upload audio / video": "Завантажити аудіо / відео",
  "Import audio": "Імпорт аудіо",
  "Import text": "Імпорт тексту",
  "Import corrected CSV": "Імпорт виправленого CSV",
  "Audio imported": "Аудіо імпортовано",
  "Audio uploaded": "Аудіо завантажено",
  "Audio deleted": "Аудіо видалено",
  "Video imported": "Відео імпортовано",
  "Video deleted": "Відео видалено",
  "Delete audio": "Видалити аудіо",
  "Delete video": "Видалити відео",
  "No audio": "Немає аудіо",
  "No preview yet": "Прев'ю ще немає",
  "Transcription settings": "Налаштування транскрипції",
  "Transcription started": "Транскрипція почалася",
  "Transcription…": "Транскрипція…",
  "Run transcription (translation is a separate step below)":
    "Запустити транскрипцію (переклад — окремий крок нижче)",
  "Source language": "Мова оригіналу",
  "Target language": "Мова перекладу",
  "Target language — also sets Step 2's generation language":
    "Мова перекладу — вона ж задає мову генерації на Кроці 2",
  "Generation language": "Мова генерації",
  "Duration (s)": "Тривалість (с)",
  "Clean audio before transcribe (Roformer)": "Очистити аудіо перед транскрипцією (Roformer)",
  "Cleaned audio (Roformer)": "Очищене аудіо (Roformer)",
  "Audio cleaning": "Очищення аудіо",
  "Off by default — raw audio usually transcribes better. Turn on only when the background is music. Playback everywhere stays the untouched original; download the cleaned file below.":
    "Типово вимкнено — сире аудіо зазвичай розпізнається краще. Вмикайте лише коли на фоні музика. Відтворення всюди лишається оригінальним; очищений файл можна завантажити нижче.",
  "Open Step 1 settings": "Відкрити налаштування Кроку 1",
  "Review the transcript & translation before moving on":
    "Перевірте транскрипцію та переклад, перш ніж рухатися далі",
  "Toggle transcription region select": "Вибір ділянок для транскрипції",
  "Remove this region": "Прибрати цю ділянку",
  "Could not save the transcription regions": "Не вдалося зберегти ділянки транскрипції",
  "Text import started": "Імпорт тексту почався",
  "Correction CSV": "CSV з виправленнями",
  "Correction CSV import started": "Імпорт CSV з виправленнями почався",
  "Transcription CSV": "CSV транскрипції",
  "Translation CSV": "CSV перекладу",
  ".srt / .txt / .docx — .srt keeps its real timestamps; .txt/.docx get auto-segmented with no timing (evenly placed at merge time in Step 3).":
    ".srt / .txt / .docx — .srt зберігає власні таймкоди; .txt/.docx автоматично розбиваються без таймінгу (рівномірно розставляються під час зведення на Кроці 3).",
  "Re-translate from the corrected transcription": "Перекласти наново з виправленої транскрипції",
  "Checked: ignores the CSV&apos;s Translation column and re-translates the corrected transcription fresh. Unchecked: uses the CSV&apos;s translation as-is.":
    "Увімкнено: колонка Translation у CSV ігнорується, виправлена транскрипція перекладається наново. Вимкнено: переклад із CSV береться як є.",

  // ── Blocks / segments ─────────────────────────────────────────────────
  "Dubbing blocks": "Блоки дубляжу",
  "Add block": "Додати блок",
  "Block note": "Нотатка до блока",
  "Note for this block": "Нотатка до цього блока",
  "Note (optional)": "Нотатка (необов'язково)",
  "Write anything worth remembering about this block…":
    "Запишіть усе, що варто пам'ятати про цей блок…",
  "Close note": "Закрити нотатку",
  "Block sizing": "Розмір блоків",
  "block sizing, export/import": "розмір блоків, експорт/імпорт",
  "applies to all blocks": "діє на всі блоки",
  "Split at cursor": "Розділити по курсору",
  "Insert a new segment right after this one — for dialogue Whisper missed entirely":
    "Вставити новий сегмент одразу після цього — для реплік, які Whisper пропустив",
  "Delete this segment — for one that shouldn't exist at all (Merge is for folding it into a neighbor instead)":
    "Видалити цей сегмент — для того, якого взагалі не має бути (щоб приєднати його до сусіднього, є «Об'єднати»)",
  "This removes the empty segment outright. Are you sure you want to delete it?":
    "Це прибере порожній сегмент повністю. Точно видалити?",
  "Could not delete block": "Не вдалося видалити блок",
  "Could not merge blocks": "Не вдалося об'єднати блоки",
  "Double-click to correct the start/end time": "Подвійний клік — виправити час початку/кінця",
  "Enter the dub script for this segment…": "Введіть текст дубляжу для цього сегмента…",
  "Re-crops audio and clears any existing dub for blocks whose Start/End changed.":
    "Перенарізає аудіо й очищає наявний дубляж для блоків, у яких змінився початок/кінець.",
  "Re-segment started": "Пересегментацію почато",
  "Re-segmented": "Пересегментовано",
  "Re-split each dub line to match the original's phrasing, so pauses land in sync. Run after translating or editing lines, before generating.":
    "Перерозбити кожен рядок дубляжу під фразування оригіналу, щоб паузи збігалися. Запускайте після перекладу чи редагування рядків, перед генерацією.",
  "This replaces every segment — any manual corrections (text edits, splits, merges, timecode fixes) will be lost. To keep them and only refresh the translation, use &quot;Translate&quot; instead.":
    "Це замінить усі сегменти — усі ручні правки (текст, розділення, об'єднання, таймкоди) буде втрачено. Щоб зберегти їх і лише оновити переклад, скористайтеся «Перекласти».",

  // ── Translation ───────────────────────────────────────────────────────
  Translate: "Перекласти",
  "Translating…": "Переклад…",
  "Translation…": "Переклад…",
  "Translation started": "Переклад почався",
  "Translate every segment's current text": "Перекласти поточний текст усіх сегментів",
  "Translate only this segment (uses the current global translation settings)":
    "Перекласти лише цей сегмент (за поточними глобальними налаштуваннями перекладу)",
  "Could not save the translation": "Не вдалося зберегти переклад",
  "Could not stop translation": "Не вдалося зупинити переклад",
  "Stop translating — cancels all pending segments (the current one finishes)":
    "Зупинити переклад — скасовує всі сегменти в черзі (поточний завершиться)",
  "AI instruction…": "Інструкція для ШІ…",
  "Extra one-shot instruction appended for the AI translation method":
    "Додаткова разова інструкція для ШІ-перекладу",
  "Write in any language — translated to English a few seconds after you stop typing. Leave empty to use the field on the right as-is.":
    "Пишіть будь-якою мовою — за кілька секунд після зупинки перекладеться англійською. Залиште порожнім, щоб поле праворуч використалося як є.",
  "Translation to voice description": "Переклад в опис голосу",

  // ── Step 2: generation, voices, references ────────────────────────────
  "Generation mode": "Режим генерації",
  "Global settings": "Глобальні налаштування",
  "Open global settings": "Відкрити глобальні налаштування",
  "Global refs": "Глобальні референси",
  "New global ref": "Новий глобальний референс",
  "Named cloning references built from this project's crops":
    "Іменовані референси для клонування, зібрані з нарізів цього проєкту",
  "Named voices to clone from": "Іменовані голоси для клонування",
  "No global reference uploaded yet.": "Глобальний референс ще не завантажено.",
  "Nothing saved yet — assemble the first one below.":
    "Ще нічого не збережено — зберіть перший нижче.",
  "Build one via the Global refs button under the player.":
    "Створіть його кнопкою «Глобальні референси» під плеєром.",
  "Global settings &rarr; Reference audio.": "Глобальні налаштування → Референсне аудіо.",
  "Reference audio · global ref": "Референсне аудіо · глобальний референс",
  "Reference source": "Джерело референсу",
  "Use a voice recorded outside this project": "Взяти голос, записаний поза цим проєктом",
  "Could not upload reference audio": "Не вдалося завантажити референсне аудіо",
  "Pick clean crops of one character, with natural pitch and intonation variety. Every character needs its own reference.":
    "Беріть чисті нарізи одного персонажа з природним діапазоном висоти та інтонацій. Кожному персонажу потрібен свій референс.",
  "No segment has a global ref picked yet — pick one, or switch to Block + Alt.":
    "Жодному сегменту ще не призначено глобальний референс — оберіть його або перемкніться на «Блок + Alt».",
  "Pick a global ref for this segment first, or switch to Block + Alt.":
    "Спершу оберіть глобальний референс для цього сегмента або перемкніться на «Блок + Alt».",
  "Needed before a voice can be rolled. It is the same field as Global Settings.":
    "Потрібно заповнити, перш ніж генерувати голос. Це те саме поле, що й у глобальних налаштуваннях.",
  "Voice Designer": "Конструктор голосу",
  "Voice description": "Опис голосу",
  "Voice description (English only)": "Опис голосу (лише англійською)",
  "Voice gender": "Стать голосу",
  "Voice locked": "Голос зафіксовано",
  "Voice locked in — every take will use it": "Голос зафіксовано — усі дублі використають його",
  "Play locked voice": "Прослухати зафіксований голос",
  "Unlock and roll again": "Розблокувати й згенерувати ще раз",
  "Roll a voice": "Згенерувати голос",
  "Roll 15s samples of the described voice and lock the one that fits":
    "Згенеруйте 15-секундні зразки описаного голосу й зафіксуйте той, що підходить",
  "Roll 15s samples of the described voice and lock the one that fits — every take then clones onto it.":
    "Згенеруйте 15-секундні зразки описаного голосу й зафіксуйте той, що підходить — далі всі дублі клонуються на нього.",
  "Generating sample&hellip;": "Генерується зразок…",
  "A warm, resonant baritone, calm and unhurried.":
    "Теплий, насичений баритон, спокійний і неквапливий.",
  Narrator: "Диктор",
  "Voices the whole script as one continuous take — the only way one voice holds across it":
    "Озвучує весь текст одним безперервним дублем — єдиний спосіб утримати один голос наскрізь",
  "Voiceover ready — the blocks now preview it": "Озвучення готове — блоки вже його програють",
  Seed: "Сід",
  "Lock seed": "Зафіксувати сід",
  "Randomize seed": "Випадковий сід",
  "Random seed on every generation": "Випадковий сід при кожній генерації",
  Pace: "Темп",
  "Reset pace to stock default": "Скинути темп до типового",
  "Reset to stock default": "Скинути до типового",
  "Perspective (shot mode)": "Крупність плану",
  "Skip already generated": "Пропускати вже згенеровані",
  "Could not stop generation": "Не вдалося зупинити генерацію",
  "Stop generation — cancels all pending blocks (the current one finishes)":
    "Зупинити генерацію — скасовує всі блоки в черзі (поточний завершиться)",
  "Stop — the current segment finishes, then the job ends":
    "Стоп — поточний сегмент завершиться, і задача зупиниться",
  "Could not stop": "Не вдалося зупинити",
  "On: match the original's pauses. Off: one continuous take.":
    "Увімк.: повторювати паузи оригіналу. Вимк.: один безперервний дубль.",
  "Download generated audio": "Завантажити згенероване аудіо",
  "Download audio": "Завантажити аудіо",

  // ── Versions, splice, additional editor ───────────────────────────────
  "Previous version": "Попередня версія",
  "Next version": "Наступна версія",
  "Previous version to splice from": "Попередня версія для вклеювання",
  "Next version to splice from": "Наступна версія для вклеювання",
  "Additional editor — splice from another version":
    "Додатковий редактор — вклеїти з іншої версії",
  "Close the Additional editor": "Закрити додатковий редактор",
  "Splice the shown take into the master at this block's timecodes — later blocks shift by the length difference":
    "Вклеїти показаний дубль у майстер за таймкодами цього блока — наступні блоки зсунуться на різницю тривалості",
  "Applied to the master take": "Застосовано до майстер-дубля",
  "Save – new version": "Зберегти – нова версія",
  "This version&apos;s audio is no longer on disk.": "Аудіо цієї версії вже немає на диску.",
  "Play both": "Відтворити обидва",

  // ── Directing tags ────────────────────────────────────────────────────
  "Directing with &lt;action&gt;": "Режисура через &lt;action&gt;",
  "How to direct the performance with <action> tags":
    "Як керувати подачею через теги <action>",
  "Insert <action></action> at the cursor — direct the emotion/delivery":
    "Вставити <action></action> у місці курсора — задати емоцію/подачу",
  "Insert an <action> emotion tag at the cursor": "Вставити тег емоції <action> у місці курсора",
  "Click a spot in the dub text first — that's where the tag goes":
    "Спершу клацніть у тексті дубляжу — саме туди стане тег",
  Examples: "Приклади",
  "Close help": "Закрити довідку",

  // ── Step 3: arrange & merge ───────────────────────────────────────────
  "Merge started": "Зведення почалося",
  "Merging...": "Зведення...",
  "Could not start merge": "Не вдалося почати зведення",
  "Dub at least one segment in Step 2 to arrange it here before merging.":
    "Продублюйте хоча б один сегмент на Кроці 2, щоб розкласти його тут перед зведенням.",
  "Track 1 is the original. Drag a take sideways to move it, up or down to put it on another track, or drag its edge to trim the head/tail for this merge only — edit the take itself in Step 2. Name the tracks after your characters, mute or solo them, and add your own audio with +. Scroll to zoom, drag the empty timeline to pan, double-click to fit — then merge.":
    "Доріжка 1 — оригінал. Тягніть дубль убік, щоб пересунути його, вгору чи вниз — щоб перекинути на іншу доріжку, або за край — щоб підрізати початок/кінець лише для цього зведення; сам дубль редагується на Кроці 2. Називайте доріжки іменами персонажів, приглушуйте їх або вмикайте соло, додавайте власне аудіо через +. Прокрутка — масштаб, перетягування порожньої шкали — панорама, подвійний клік — вписати. Далі зводьте.",
  "Mute, solo and track changes only reach the audio on the next merge — what you hear now is the previous one. Re-merge to apply them.":
    "Приглушення, соло та зміни доріжок потраплять в аудіо лише при наступному зведенні — зараз ви чуєте попереднє. Зведіть ще раз, щоб застосувати.",
  "Add your own audio file to this track": "Додати власний аудіофайл на цю доріжку",
  "Delete this clip": "Видалити цей кліп",
  "Remove from the timeline": "Прибрати зі шкали",
  "Reset to original position": "Повернути на початкову позицію",
  "Could not save position": "Не вдалося зберегти позицію",
  "Could not move that file": "Не вдалося перемістити цей файл",
  "Could not remove that file": "Не вдалося прибрати цей файл",
  "Could not update the track": "Не вдалося оновити доріжку",
  "Discard unsaved edits and reset this clip": "Скасувати незбережені правки й скинути цей кліп",
  "Discard unsaved edits and reset to the original single clip":
    "Скасувати незбережені правки й повернутися до початкового одного кліпу",
  "Trim head (this merge only)": "Підрізати початок (лише для цього зведення)",
  "Trim tail (this merge only)": "Підрізати кінець (лише для цього зведення)",
  "Reset trim (restore the full take for this merge)":
    "Скинути підрізку (повернути повний дубль для цього зведення)",
  "Drag to trim the start": "Тягніть, щоб підрізати початок",
  "Drag to trim the end": "Тягніть, щоб підрізати кінець",
  "Bigger board": "Більша дошка",
  "Smaller board": "Менша дошка",
  "Grow the board, more room to arrange": "Збільшити дошку — більше місця для монтажу",
  "Shrink the board, give the picture more room": "Зменшити дошку — більше місця для зображення",
  "No original audio — blocks are spaced evenly across this length":
    "Оригінального аудіо немає — блоки рівномірно розставлені на цій тривалості",
  "Download stems": "Завантажити стеми",
  "No stems yet — run a merge first.": "Стемів ще немає — спершу зведіть.",
  "Pick what goes in the archive": "Оберіть, що покласти в архів",
  "Full mix (everything in one file)": "Повний мікс (усе одним файлом)",
  "All (.zip)": "Усе (.zip)",
  "One archive with every file above that exists — missing pieces are simply skipped":
    "Один архів з усіма наявними файлами вище — відсутні просто пропускаються",

  // ── Subtitles ─────────────────────────────────────────────────────────
  Subtitles: "Субтитри",
  "Style the subtitles, download .srt / .vtt, or burn them into the video":
    "Оформте субтитри, завантажте .srt / .vtt або вшийте їх у відео",
  "Subtitle preview on a frame from the video": "Прев'ю субтитрів на кадрі з відео",
  "Download video with subtitles": "Завантажити відео із субтитрами",
  "Video with subtitles is ready": "Відео із субтитрами готове",
  Font: "Шрифт",
  "Background box": "Підкладка",
  "Draw a solid box behind the text": "Малювати суцільну підкладку під текстом",
  "Reset style": "Скинути оформлення",
  "Back to the default look": "Повернути типовий вигляд",
  "Name this style…": "Назвіть це оформлення…",
  "Style saved": "Оформлення збережено",
  "Could not save the subtitle style": "Не вдалося зберегти оформлення субтитрів",
  "Could not delete the style": "Не вдалося видалити оформлення",
  "Nothing saved yet — a saved style is reusable in every project.":
    "Ще нічого не збережено — збережене оформлення можна використати в будь-якому проєкті.",
  "Cues come straight from each segment&apos;s timecodes — retime a segment and the next export follows it. Segments with no text in the chosen language are left out rather than shown as blank cards.":
    "Репліки беруться просто з таймкодів кожного сегмента — зміните таймінг, і наступний експорт це врахує. Сегменти без тексту обраною мовою не потрапляють в експорт, а не показуються порожніми.",

  // ── Player, waveform ──────────────────────────────────────────────────
  "Hide mini player": "Сховати міні-плеєр",
  "Drag to move the mini player": "Тягніть, щоб пересунути міні-плеєр",
  "Drag to resize the mini player": "Тягніть, щоб змінити розмір міні-плеєра",
  "Drag to scrub — seeks the video": "Тягніть, щоб перемотати відео",
  "Waveform vertical zoom": "Вертикальний масштаб хвилі",

  // ── Jobs, admin, misc ─────────────────────────────────────────────────
  Jobs: "Задачі",
  "Last job": "Остання задача",
  Joined: "Приєднався",
  "Recent activity": "Остання активність",
  "No activity yet.": "Активності ще немає.",
  "Accounts, usage, and manual credit grants": "Акаунти, використання та ручні нарахування",
  "Grant credits": "Нарахувати кредити",
  "Grants are recorded in the same ledger as automatic charges, so they appear in the account&apos;s own transaction history. A negative amount reverses a grant.":
    "Нарахування записуються в той самий журнал, що й автоматичні списання, тому їх видно в історії операцій акаунта. Від'ємна сума скасовує нарахування.",
  "Filter accounts": "Фільтр акаунтів",
  "Filter by email or name": "Фільтр за поштою або іменем",
  "Could not save changes": "Не вдалося зберегти зміни",
  "Could not save settings": "Не вдалося зберегти налаштування",
  "Close settings": "Закрити налаштування",
  "Self-serve checkout is coming soon. For now, requesting a plan sends us a message and we&apos;ll set it up and top up your balance directly.":
    "Самостійна оплата з'явиться незабаром. Поки що запит на тариф надсилає нам повідомлення — ми все налаштуємо й поповнимо ваш баланс напряму.",
  "Request sent — we'll follow up to set up your plan.":
    "Запит надіслано — ми звʼяжемося й налаштуємо ваш тариф.",
  "AI translate": "ШІ-переклад",
  "Google Translate": "Google Translate",
  Regions: "Ділянки",
  "Auto-scroll": "Автоскрол",
  "Name (optional)": "Ім'я (необов'язково)",

  // ── Second pass: controls, states and confirmations ───────────────────
  "Full name": "Повне ім'я",
  "Save changes": "Зберегти зміни",
  "Save name": "Зберегти імʼя",
  "Saving…": "Збереження…",
  "Update password": "Оновити пароль",
  "Updating…": "Оновлення…",
  "Confirm with your password": "Підтвердіть своїм паролем",
  "Permanently delete everything": "Назавжди видалити все",
  "Deleting…": "Видалення…",
  "Create account": "Створити акаунт",
  "Creating account...": "Створення акаунта...",
  "Request this plan": "Замовити цей тариф",
  "Sending…": "Надсилання…",
  Rename: "Перейменувати",
  Upload: "Завантажити",
  "Uploading…": "Завантаження…",
  "Importing…": "Імпорт…",
  Grant: "Нарахувати",
  "Granting…": "Нарахування…",
  "No accounts yet.": "Акаунтів ще немає.",
  "No account matches that filter.": "Жоден акаунт не підходить під фільтр.",
  Job: "Задача",
  Runtime: "Тривалість",
  Blocks: "Блоки",
  Dubbed: "Продубльовано",
  Translation: "Переклад",
  Text: "Текст",

  // Step 1 — import, transcription
  "Import video": "Імпорт відео",
  "Import video only": "Імпорт лише відео",
  "Import external ref": "Імпорт зовнішнього референсу",
  "No source media uploaded": "Вихідні медіа не завантажено",
  "This project has no source video": "У цьому проєкті немає вихідного відео",
  "no source video": "немає вихідного відео",
  "Audio-only project": "Проєкт лише з аудіо",
  "Start transcription?": "Почати транскрипцію?",
  "Yes, start": "Так, почати",
  "Re-transcribe from scratch?": "Транскрибувати наново з нуля?",
  "Yes, re-transcribe": "Так, транскрибувати наново",
  "Re-segmenting…": "Пересегментація…",
  "Min duration (s)": "Мін. тривалість (с)",
  "Max duration (s)": "Макс. тривалість (с)",
  "Region select: on — drag the waveform to mark what gets transcribed":
    "Вибір ділянок: увімк. — тягніть по хвилі, щоб позначити, що транскрибувати",
  "Region select: off — the waveform behaves normally":
    "Вибір ділянок: вимк. — хвиля працює як звичайно",
  "Auto-scroll: on": "Автопрокрутка: увімк.",
  "Auto-scroll: off": "Автопрокрутка: вимк.",
  "No segment has text yet.": "Жоден сегмент ще не має тексту.",
  "No segment has been translated yet — translate in Step 1, or preview the original.":
    "Жоден сегмент ще не перекладено — перекладіть на Кроці 1 або дивіться оригінал.",

  // Blocks
  "Add new block": "Додати новий блок",
  "Delete block": "Видалити блок",
  "Split block": "Розділити блок",
  "Merge with next": "Обʼєднати з наступним",
  "Merge with next block": "Обʼєднати з наступним блоком",
  "No next block to merge with": "Немає наступного блока для обʼєднання",
  "Additional editor": "Додатковий редактор",

  // Generation
  Generate: "Згенерувати",
  "Generate all": "Згенерувати все",
  "Generate this block": "Згенерувати цей блок",
  "Generate another version to splice from": "Згенерувати ще версію для вклеювання",
  "No take yet — press GENERATE": "Дубля ще немає — натисніть ЗГЕНЕРУВАТИ",
  "Nothing to stop": "Нічого зупиняти",
  "Preview this take": "Прослухати цей дубль",
  "Lock this take": "Зафіксувати цей дубль",
  "Unlock this take": "Розфіксувати цей дубль",
  "Lock this take so it's never auto-deleted (max 3 per segment)":
    "Зафіксувати цей дубль, щоб його ніколи не видалило автоматично (максимум 3 на сегмент)",
  "Locked — kept on disk regardless of age. Click to unlock.":
    "Зафіксовано — зберігається на диску незалежно від віку. Натисніть, щоб розфіксувати.",
  "Re-voice": "Переозвучити",
  "Re-voice this paragraph": "Переозвучити цей абзац",
  "Voice the script": "Озвучити текст",
  "Voicing…": "Озвучення…",
  Guidance: "Скерування",
  Steps: "Кроки",
  "Speech validation": "Перевірка мовлення",
  "Silence threshold (dB)": "Поріг тиші (дБ)",
  "Pauses on": "Паузи увімк.",
  "Pauses off": "Паузи вимк.",

  // Voice designer
  "Design voice": "Створити голос",
  "Roll until one fits, then lock it in": "Крутіть, доки не підійде, і зафіксуйте",
  "Voice locked — open designer": "Голос зафіксовано — відкрити конструктор",
  "Voice committed to this project": "Голос закріплено за цим проєктом",

  // Player
  Play: "Пуск",
  Pause: "Пауза",
  "Play preview": "Відтворити прев'ю",
  "Pause preview": "Пауза прев'ю",
  Preview: "Прев'ю",
  "Play the merged timeline from the playhead": "Відтворити зведену шкалу від курсора",

  // Step 3 — merge, tracks, stems
  Merge: "Звести",
  "Re-merge": "Звести знову",
  "Merge every dubbed block into the final track — the player picks up the new sound":
    "Звести всі продубльовані блоки у фінальну доріжку — плеєр підхопить новий звук",
  "Rendering audio…": "Рендер аудіо…",
  "Rendering video…": "Рендер відео…",
  "Processing on the server — this can take a while for a long file…":
    "Обробка на сервері — для довгого файлу це може зайняти час…",
  "Set the total duration first (no original audio to infer it from)":
    "Спершу задайте загальну тривалість (оригінального аудіо, з якого її взяти, немає)",
  "Mute this track": "Приглушити цю доріжку",
  "Muted — not in the export": "Приглушено — не потрапить в експорт",
  "Solo this track": "Соло цієї доріжки",
  "Soloed — only soloed tracks export": "Соло — в експорт підуть лише соло-доріжки",
  "Download .zip": "Завантажити .zip",
  "Packing…": "Пакування…",
  "Pick which tracks — or the full mix — to download":
    "Оберіть, які доріжки — або повний мікс — завантажити",
  "run a merge first": "спершу зведіть",

  // Subtitles
  "Burn into video": "Вшити у відео",
  "Re-encodes the video with the subtitles drawn into the picture":
    "Перекодовує відео із субтитрами, вписаними в зображення",
  "Box color": "Колір підкладки",
  "Box opacity": "Прозорість підкладки",
  Outline: "Обведення",
  "Outline width": "Товщина обведення",
  Shadow: "Тінь",
  Size: "Розмір",
  "Side margin": "Бічний відступ",
  "Vertical margin": "Вертикальний відступ",

  // Sharing states
  "Full access — you can edit and generate in this project":
    "Повний доступ — ви можете редагувати й генерувати в цьому проєкті",
  "Read-only — you can watch, listen and read, but not change anything":
    "Лише перегляд — можна дивитися, слухати й читати, але нічого не змінювати",
};
